import React, { Fragment, useMemo, useState } from "react";
import { formatCurrency } from "../../../helpers/estimateTotals";
import { formatAreaSqft, formatDeductionType } from "../../../helpers/measurementModel";
import { featureDetail } from "./summaryReadout";

function findWorkType(estimateTree, featureId) {
  for (const location of estimateTree?.locations || []) {
    const workType = (location.workTypes || []).find((item) => item.estimate_feature_id === featureId);
    if (workType) return workType;
  }
  return null;
}

function FeatureBody({ workType, materialsCatalog }) {
  const detail = featureDetail(workType, materialsCatalog);
  if (!detail.measurements.length && !detail.materials.length && !detail.labour.length) {
    return <p className="est-lf__empty">No lines yet.</p>;
  }
  return (
    <div className="est-lf__detail">
      {detail.measurements.length ? (
        <div>
          <p className="est-lf__kicker">Measurements</p>
          {detail.measurements.map((area) => (
            <div key={area.id} className="est-lf__block">
              <div className="est-lf__line">
                <span>{area.name}{area.dimensions ? ` · ${area.dimensions}` : ""}</span>
                <span>Gross {formatAreaSqft({ area_sqft: area.gross })}</span>
              </div>
              {area.deductions.map((item) => (
                <div key={item.id} className="est-lf__line is-sub">
                  <span>− {item.name}{item.dimensions ? ` · ${item.dimensions}` : ""}</span>
                  <span>− {formatAreaSqft({ area_sqft: item.sqft })}</span>
                </div>
              ))}
              {area.legacyShare > 0.05 ? (
                <div className="est-lf__line is-sub"><span>− other {area.surface} deductions</span><span>− {area.legacyShare.toFixed(1)} ft²</span></div>
              ) : null}
              <div className="est-lf__line is-net"><span>Net</span><span>{area.net.toFixed(1)} ft²</span></div>
            </div>
          ))}
          {detail.unlinked.map((item) => (
            <div key={item.section_id} className="est-lf__line is-sub">
              <span>− {formatDeductionType(item.deduction_type)} · all {item.applies_to_surface_type || "surfaces"}</span>
              <span>− {formatAreaSqft(item)}</span>
            </div>
          ))}
        </div>
      ) : null}
      {detail.materials.length ? (
        <div>
          <p className="est-lf__kicker">Materials</p>
          {detail.materials.map((row) => (
            <div key={row.id} className="est-lf__line">
              <span>
                {row.name}
                {row.quantity != null ? ` · ${row.quantity} ${row.unit}` : ""}
                {` · ${formatCurrency(row.unitPrice)}`}
                {row.adjusted ? <span className="eg-mark">Adjusted</span> : null}
                {row.adjustment ? <span className="est-lf__adj">{row.adjustment}</span> : null}
              </span>
              <span>{formatCurrency(row.total)}</span>
            </div>
          ))}
        </div>
      ) : null}
      {detail.labour.length ? (
        <div>
          <p className="est-lf__kicker">Labour</p>
          {detail.labour.map((row) => (
            <div key={row.id} className="est-lf__line">
              <span>
                {row.name}
                {` · ${Number(row.quantity).toFixed(row.unit === "ft²" ? 1 : 0)} ${row.unit}`}
                {row.rate != null && row.rate !== "" ? ` · ${formatCurrency(row.rate)}` : ""}
                {row.adjusted ? <span className="eg-mark">Adjusted</span> : null}
                {row.adjustment ? <span className="est-lf__adj">{row.adjustment}</span> : null}
                {row.surfaces.length ? ` · ${row.surfaces.join(", ")}` : ""}
              </span>
              <span>{formatCurrency(row.total)}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Desktop summary: locations are bold collapsible rows, features sit indented
 * under them, and each feature opens to its measurements, materials and labour.
 * Collapsed by default.
 */
export function LocationsFeaturesPanel({ locations, estimateTree, materialsCatalog, onEditFeature }) {
  const [openLocations, setOpenLocations] = useState({});
  const [openFeatures, setOpenFeatures] = useState({});
  const locationIds = useMemo(() => locations.map((location) => location.locationId), [locations]);
  const featureIds = useMemo(
    () => locations.flatMap((location) => location.workTypes.map((workType) => workType.workTypeId)),
    [locations],
  );

  function expandAll() {
    setOpenLocations(Object.fromEntries(locationIds.map((id) => [id, true])));
    setOpenFeatures(Object.fromEntries(featureIds.map((id) => [id, true])));
  }
  function collapseAll() {
    setOpenLocations({});
    setOpenFeatures({});
  }

  return (
    <div data-testid="locations-features">
      <div className="est-lf__tools">
        <button type="button" className="eg-link" onClick={expandAll}>Expand all</button>
        <button type="button" className="eg-link" onClick={collapseAll}>Collapse all</button>
      </div>
      <table className="est-lf">
        <thead>
          <tr>
            <th>Location / Feature</th>
            <th className="est-lf__num">Materials</th>
            <th className="est-lf__num">Labour</th>
            <th className="est-lf__num">Total</th>
          </tr>
        </thead>
        <tbody>
          {locations.map((location) => {
            const locationOpen = Boolean(openLocations[location.locationId]);
            const materials = location.workTypes.reduce((sum, workType) => sum + workType.materialsTotal, 0);
            const labour = location.workTypes.reduce((sum, workType) => sum + workType.labourTotal, 0);
            return (
              <Fragment key={location.locationId}>
                <tr className="est-lf__loc">
                  <th scope="row">
                    <button type="button" className="est-lf__toggle" aria-expanded={locationOpen} onClick={() => setOpenLocations((prev) => ({ ...prev, [location.locationId]: !locationOpen }))}>
                      {locationOpen ? "▾" : "▸"} {location.locationName}
                    </button>
                  </th>
                  <td className="est-lf__num">{formatCurrency(materials)}</td>
                  <td className="est-lf__num">{formatCurrency(labour)}</td>
                  <td className="est-lf__num">{formatCurrency(location.locationTotal)}</td>
                </tr>
                {locationOpen ? location.workTypes.map((workType) => {
                  const featureOpen = Boolean(openFeatures[workType.workTypeId]);
                  const source = findWorkType(estimateTree, workType.workTypeId);
                  return (
                    <Fragment key={workType.workTypeId}>
                      <tr className="est-lf__feat">
                        <th scope="row">
                          <button type="button" className="est-lf__toggle" aria-expanded={featureOpen} onClick={() => setOpenFeatures((prev) => ({ ...prev, [workType.workTypeId]: !featureOpen }))}>
                            {featureOpen ? "▾" : "▸"} {workType.label}
                          </button>
                          {onEditFeature ? (
                            <button type="button" className="eg-link" onClick={() => onEditFeature(workType.workTypeId)}>Edit</button>
                          ) : null}
                        </th>
                        <td className="est-lf__num">{formatCurrency(workType.materialsTotal)}</td>
                        <td className="est-lf__num">{formatCurrency(workType.labourTotal)}</td>
                        <td className="est-lf__num">{formatCurrency(workType.workTypeTotal)}</td>
                      </tr>
                      {featureOpen && source ? (
                        <tr className="est-lf__body">
                          <td colSpan={4}>
                            <FeatureBody workType={source} materialsCatalog={materialsCatalog} />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                }) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
